import type { ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { CampaignTotpStepUpModal } from '@/components/CampaignTotpStepUp/CampaignTotpStepUpModal'
import { getTotpStepUpStatus, verifyTotpStepUp } from '@/lib/api/two-factor.service'
import { ApiClientError } from '@/lib/api/client'
import {
  CAMPAIGN_TOTP_STEP_UP_COPY,
  getTotpStepUpPromptReason,
  isCampaignTotpStepUpError,
  isTotpStepUpStillElevated,
  type TotpStepUpCopy,
  type TotpStepUpPromptReason,
} from '@/lib/campaign-totp-step-up'
import { useAuth } from '@/hooks/useAuth'
import type { TotpStepUpStatus } from '@/types/two-factor'

export type CampaignTotpStepUpContextValue = {
  ensureElevated: (forceRefresh?: boolean) => Promise<boolean>
  runWithStepUp: <T>(action: () => Promise<T>) => Promise<T>
}

const CampaignTotpStepUpContext = createContext<CampaignTotpStepUpContextValue | undefined>(
  undefined
)

const EMPTY_STATUS: TotpStepUpStatus = {
  two_factor_enabled: false,
  elevated: false,
  expires_at: null,
}

const MAX_STEP_UP_PROMPTS = 2

type PendingResolve = (value: boolean) => void
type ModalPromptReason = TotpStepUpPromptReason | 'unavailable'

export function CampaignTotpStepUpProvider({
  children,
  copy = CAMPAIGN_TOTP_STEP_UP_COPY,
}: {
  children: ReactNode
  copy?: TotpStepUpCopy
}) {
  const { guard } = useAuth()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isLoadingStatus, setIsLoadingStatus] = useState(false)
  const [code, setCode] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [promptReason, setPromptReason] = useState<ModalPromptReason>('code')
  const pendingResolversRef = useRef<PendingResolve[]>([])
  const isModalOpenRef = useRef(false)
  const statusRequestRef = useRef<Promise<TotpStepUpStatus> | null>(null)
  const ensureRequestRef = useRef<Promise<boolean> | null>(null)
  const statusRef = useRef<TotpStepUpStatus | null>(null)
  const statusGenerationRef = useRef(0)

  const resolvePending = useCallback((value: boolean) => {
    const resolvers = pendingResolversRef.current
    pendingResolversRef.current = []
    resolvers.forEach((resolve) => resolve(value))
  }, [])

  const invalidateLocalElevation = useCallback(() => {
    statusGenerationRef.current += 1
    statusRequestRef.current = null
    ensureRequestRef.current = null
    setIsLoadingStatus(false)
    if (statusRef.current) {
      statusRef.current = { ...statusRef.current, elevated: false }
    }
  }, [])

  const loadStatus = useCallback(async (): Promise<TotpStepUpStatus> => {
    if (guard !== 'admin' && guard !== 'institution') {
      statusRef.current = EMPTY_STATUS
      return EMPTY_STATUS
    }

    if (statusRequestRef.current) {
      return statusRequestRef.current
    }

    const generation = statusGenerationRef.current
    setIsLoadingStatus(true)

    const request = getTotpStepUpStatus({ guard }).then((next) => {
      if (generation !== statusGenerationRef.current) {
        return next
      }
      statusRef.current = next
      return next
    })

    statusRequestRef.current = request

    return request.finally(() => {
      if (statusRequestRef.current === request) {
        statusRequestRef.current = null
      }
      if (generation === statusGenerationRef.current) {
        setIsLoadingStatus(false)
      }
    })
  }, [guard])

  const openModal = useCallback((reason: ModalPromptReason): Promise<boolean> => {
    return new Promise((resolve) => {
      pendingResolversRef.current.push(resolve)
      if (isModalOpenRef.current) {
        return
      }
      isModalOpenRef.current = true
      setCode('')
      setPromptReason(reason)
      setIsModalOpen(true)
    })
  }, [])

  const ensureElevated = useCallback(
    async (forceRefresh = false): Promise<boolean> => {
      if (guard !== 'admin' && guard !== 'institution') {
        return false
      }

      if (!forceRefresh && isTotpStepUpStillElevated(statusRef.current)) {
        return true
      }

      if (ensureRequestRef.current) {
        return ensureRequestRef.current
      }

      const request = (async () => {
        try {
          const current = await loadStatus()
          if (isTotpStepUpStillElevated(current)) {
            return true
          }
          const reason: ModalPromptReason = current.two_factor_enabled ? 'code' : 'enrollment'
          setErrorMessage(null)
          return openModal(reason)
        } catch {
          statusRef.current = null
          setErrorMessage('Não foi possível verificar a autenticação em duas etapas.')
          return openModal('unavailable')
        } finally {
          ensureRequestRef.current = null
        }
      })()

      ensureRequestRef.current = request
      return request
    },
    [guard, loadStatus, openModal]
  )

  const closeModal = useCallback(
    (success: boolean) => {
      isModalOpenRef.current = false
      setIsModalOpen(false)
      setCode('')
      setIsVerifying(false)
      if (!success) {
        setErrorMessage(null)
      }
      resolvePending(success)
    },
    [resolvePending]
  )

  const handleVerify = useCallback(async () => {
    if (guard !== 'admin' && guard !== 'institution') {
      return
    }

    if (code.length !== 6) {
      setErrorMessage('Informe o código de 6 dígitos do autenticador.')
      return
    }

    setIsVerifying(true)
    setErrorMessage(null)

    try {
      const next = await verifyTotpStepUp(code, { guard })
      statusRef.current = next
      closeModal(true)
    } catch (error) {
      if (error instanceof ApiClientError) {
        setErrorMessage(error.message)
      } else {
        setErrorMessage('Não foi possível confirmar o código. Tente novamente.')
      }
      setIsVerifying(false)
    }
  }, [closeModal, code, guard])

  const promptAfterForbidden = useCallback(
    async (error: ApiClientError): Promise<boolean> => {
      if (guard !== 'admin' && guard !== 'institution') {
        return false
      }

      invalidateLocalElevation()
      setErrorMessage(null)
      return openModal(getTotpStepUpPromptReason(error))
    },
    [guard, invalidateLocalElevation, openModal]
  )

  const runWithStepUp = useCallback(
    async <T,>(action: () => Promise<T>): Promise<T> => {
      let promptsUsed = 0

      while (true) {
        try {
          return await action()
        } catch (error) {
          if (!isCampaignTotpStepUpError(error)) {
            throw error
          }

          if (promptsUsed >= MAX_STEP_UP_PROMPTS) {
            throw error
          }

          const elevated = await promptAfterForbidden(error)
          if (!elevated) {
            throw error
          }

          promptsUsed += 1
        }
      }
    },
    [promptAfterForbidden]
  )

  const value = useMemo(
    () => ({
      ensureElevated,
      runWithStepUp,
    }),
    [ensureElevated, runWithStepUp]
  )

  return (
    <CampaignTotpStepUpContext.Provider value={value}>
      {children}
      {isModalOpen ? (
        <CampaignTotpStepUpModal
          code={code}
          enrollmentHint={copy.enrollmentHint}
          errorMessage={errorMessage}
          isEnrollmentRequired={promptReason === 'enrollment'}
          isLoadingStatus={isLoadingStatus && promptReason !== 'code' && promptReason !== 'enrollment'}
          isStatusUnavailable={promptReason === 'unavailable'}
          isVerifying={isVerifying}
          subtitle={copy.subtitle}
          onCancel={() => closeModal(false)}
          onCodeChange={(next) => {
            setCode(next)
            setErrorMessage(null)
          }}
          onVerify={() => {
            void handleVerify()
          }}
        />
      ) : null}
    </CampaignTotpStepUpContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- hook exportado junto ao provider (padrão de contexto)
export function useCampaignTotpStepUp(): CampaignTotpStepUpContextValue {
  const context = useContext(CampaignTotpStepUpContext)
  if (context === undefined) {
    throw new Error('useCampaignTotpStepUp must be used within CampaignTotpStepUpProvider')
  }
  return context
}
